// @ts-nocheck // There are just too many issues in this file to fix it right away.
import axios from 'axios';

import type { AxiosRequestConfig, Method } from 'axios';

import Config from '../Config';
import {
    convertCentsToDollars,
    convertDollarsToCents,
    getCurrentCompanyId,
    getCurrentOnBehalfOfUserId,
    getCurrentUserId,
    getToken,
    signoutWhenExpiredToken,
} from '../Lib/Utils';

const listCompanyAction = [];

const headers = { 'Content-Type': 'application/json' };

const instance = axios.create({
    baseURL: Config.api_url,
    headers: {
        'Content-Type': 'application/json',
    },
});

instance.interceptors.response.use(
    (response) => response,
    async (error) => {
        await signoutWhenExpiredToken();
        throw error;
    }
);

const getQueryParams = (params) => {
    const query = Object.keys(params)
        .map((k) => `${encodeURIComponent(k)}=${encodeURIComponent(params[k])}`)
        .join('&');
    return query;
};

export const getAPI = async (action, apiParams?, isPublic?) => {
    let params = apiParams || '';
    if (params) {
        params = `?${getQueryParams(params)}`;
    }

    if (listCompanyAction.includes(action)) {
        const CompanyId = getCurrentCompanyId();
        params += `${params ? `&` : '?'}CompanyId=${CompanyId}`;
    }

    if (!isPublic) {
        const tokenToUse = await getToken();

        instance.defaults.headers.common.Authorization = tokenToUse;
    }
    return instance.get(`${action}${params}`).then((response) => (response.data && response.data.body) || {});
};

export const isLocalStoragePossiblyBroken = () => !(getCurrentCompanyId() && getCurrentUserId());

export const requestWithoutOverride = async ({
    url,
    queryParams,
    payload,
    token,
    method = 'GET',
    usePaytronHeader = false,
    useJSONContentType = false,
    useToken = true,
}: {
    url: string | URL;
    queryParams?: unknown;
    payload: unknown;
    token?: string;
    method: Method;
    /**
     * Whether to use X-Paytron-Account header for specifying company.
     * @default false // INSPECT: Why is this false by default? Seems like it should be true by default. Kept like the more specific functions so that we don't accidentally change behaviour when switching over.
     */
    usePaytronHeader: boolean;
    /**
     * Whether to use application/json (if true) or application/vnd.api+json (if false) as the content type.
     * @default false (uses application/vnd.api+json by default) // INSPECT: Why is this false by default? Seems like it should be true by default. Kept like the more specific functions so that we don't accidentally change behaviour when switching over.
     */
    useJSONContentType: boolean;
    /**
     * Whether to use token in Authorization header (not needed for public endpoints that don't require auth)
     * @default true
     */
    useToken?: boolean;
}) => {
    let tokenToUse: string | null | undefined = token;
    if (useToken && !token) {
        tokenToUse = await getToken();
    }

    if (usePaytronHeader) tokenToUse = `Bearer ${tokenToUse}`;

    return axios({
        url: url.toString(),
        method,
        params: queryParams,
        data: payload,
        headers: {
            ...headers,
            ...(useToken && { authorization: tokenToUse }),
            ...(usePaytronHeader && { 'X-Paytron-Account': getCurrentCompanyId() }),
            ...(usePaytronHeader && {
                'Content-Type': useJSONContentType ? 'application/json' : 'application/vnd.api+json',
            }),
        },
    });
};

export const getWithoutOverride = async <
    TResponseBody = any // eslint-disable-line @typescript-eslint/no-explicit-any -- // FIXME: this really should be `unknown` but it causes too many errors with existing code not setting it
>(
    action: string | URL,
    queryParams,
    token,
    usePaytronHeader = false,
    useJSONContentType = false,
    config: AxiosRequestConfig = {}
) => {
    let tokenToUse = token;
    if (!token) {
        tokenToUse = await getToken();
    }

    if (usePaytronHeader) tokenToUse = `Bearer ${tokenToUse}`;

    return axios.get<TResponseBody>(action.toString(), {
        params: queryParams,
        ...config,
        headers: {
            ...headers,
            authorization: tokenToUse,
            ...(usePaytronHeader && { 'X-Paytron-Account': getCurrentCompanyId() }),
            ...(usePaytronHeader && {
                'Content-Type': useJSONContentType ? 'application/json' : 'application/vnd.api+json',
            }),
            ...config.headers,
        },
    });
};

export const postWithoutOverride = async <TResponseBody = unknown>(
    action: string | URL,
    data,
    jwt,
    usePaytronHeader = false,
    useJSONContentType = false
) => {
    const companyId = getCurrentCompanyId();
    const params = data || {};
    if (!jwt && listCompanyAction.includes(action.toString())) {
        params.CompanyId = companyId;
    }
    let token = jwt;
    if (!jwt) {
        token = await getToken();
    }

    if (usePaytronHeader) token = `Bearer ${token}`;

    return axios.post<TResponseBody>(action.toString(), params, {
        headers: {
            ...headers,
            authorization: token,
            ...(usePaytronHeader && { 'X-Paytron-Account': companyId }),
            ...(usePaytronHeader && {
                'Content-Type': useJSONContentType ? 'application/json' : 'application/vnd.api+json',
            }),
        },
    });
};

export const patchWithoutOverride = async (
    action: string | URL,
    apiParams,
    jwt,
    usePaytronHeader = false,
    useJSONContentType = false
) => {
    const companyId = getCurrentCompanyId();
    const onBehalfOfUserId = getCurrentOnBehalfOfUserId();
    const params = apiParams || {};
    if (!jwt && listCompanyAction.includes(action)) {
        params.CompanyId = companyId;
    }
    let token = jwt;
    if (!jwt) {
        token = await getToken();
    }

    if (usePaytronHeader) token = `Bearer ${token}`;

    return axios.patch(action.toString(), params, {
        headers: {
            ...headers,
            authorization: token,
            ...(usePaytronHeader && { 'X-Paytron-Account': companyId }),
            ...(usePaytronHeader && {
                'Content-Type': useJSONContentType ? 'application/json' : 'application/vnd.api+json',
            }),
            ...(onBehalfOfUserId && { 'X-On-Behalf-Of': onBehalfOfUserId }),
        },
    });
};

export const putWithoutOverride = async (
    action: string | URL,
    apiParams,
    jwt,
    usePaytronHeader = false,
    useJSONContentType = false
) => {
    const companyId = getCurrentCompanyId();
    const params = apiParams || {};
    if (!jwt && listCompanyAction.includes(action)) {
        params.CompanyId = companyId;
    }
    let token = jwt;
    if (!jwt) {
        token = await getToken();
    }
    if (usePaytronHeader) token = `Bearer ${token}`;

    return axios.put(action.toString(), params, {
        headers: {
            ...headers,
            authorization: token,
            ...(usePaytronHeader && { 'X-Paytron-Account': companyId }),
            ...(usePaytronHeader && {
                'Content-Type': useJSONContentType ? 'application/json' : 'application/vnd.api+json',
            }),
        },
    });
};

export const httpDelete = async (
    action: string | URL,
    apiParams,
    jwt,
    usePaytronHeader = false,
    useJSONContentType = false
) => {
    const companyId = getCurrentCompanyId();
    const params = apiParams || {};
    if (!jwt && listCompanyAction.includes(action)) {
        params.CompanyId = getCurrentCompanyId();
    }
    let token = jwt;
    if (!jwt) {
        token = await getToken();
    }

    if (usePaytronHeader) token = `Bearer ${token}`;

    return axios.delete(action.toString(), {
        data: params,
        headers: {
            ...headers,
            authorization: token,
            ...(usePaytronHeader && { 'X-Paytron-Account': companyId }),
            ...(usePaytronHeader && {
                'Content-Type': useJSONContentType ? 'application/json' : 'application/vnd.api+json',
            }),
        },
    });
};

export const postAPI = async (action, apiParams) => {
    const params = apiParams || {};
    if (listCompanyAction.includes(action)) {
        params.CompanyId = getCurrentCompanyId();
    }

    instance.defaults.headers.common.Authorization = await getToken();
    return instance.post(action, params).then((response) => (response.data && response.data.body) || response || {});
};

export const putAPI = async (action, apiParams) => {
    const params = apiParams || {};
    if (listCompanyAction.includes(action)) {
        params.CompanyId = getCurrentCompanyId();
    }

    instance.defaults.headers.common.Authorization = await getToken();

    return instance.put(action, params).then((response) => (response.data && response.data.body) || response || {});
};

/**
 *
 * @param amount
 * @param fixedSide
 * @param sellCurrency
 * @param beneficiaryId
 * @param quoteDate
 * @returns {Promise<{fees: {transactionFee, fxMarginPercent}, buyAmount, wholesaleRate: number, sellAmount, fixedSide, validUntil, id, sellCurrency, buyCurrency, retailRate: number}>}
 */
export const createQuote = async ({ amount, fixedSide, sellCurrency, beneficiaryId, quoteDate }) => {
    const amountInCents = convertDollarsToCents(amount, sellCurrency);
    const token = await getToken();
    const companyId = getCurrentCompanyId();
    const quoteRequest = await fetch(`${Config.quotes_json_api_v1}/`, {
        method: 'POST',
        headers: {
            Accept: 'application/vnd.api+json; version=1',
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/vnd.api+json; version=1',
            'X-Paytron-Account': companyId,
        },
        body: JSON.stringify({
            data: {
                type: 'quotes',
                attributes: {
                    amount: amountInCents,
                    fixedSide: fixedSide.toUpperCase(),
                    sellCurrency,
                    ...(quoteDate && { quoteDate }),
                },
                relationships: {
                    beneficiary: {
                        data: {
                            id: beneficiaryId,
                            type: 'beneficiary',
                        },
                    },
                },
            },
        }),
    });
    const quoteResponse = await quoteRequest.json();

    if (!quoteResponse?.data?.id) {
        return quoteResponse;
    }

    return {
        id: quoteResponse.data.id,
        buyAmount: convertCentsToDollars(
            parseInt(quoteResponse.data.attributes.buyAmount, 10),
            quoteResponse.data.attributes.buyCurrency
        ),
        buyCurrency: quoteResponse.data.attributes.buyCurrency,
        sellAmount: convertCentsToDollars(
            parseInt(quoteResponse.data.attributes.sellAmount, 10),
            quoteResponse.data.attributes.sellCurrency
        ),
        sellCurrency: quoteResponse.data.attributes.sellCurrency,
        fees: {
            transactionFee: convertCentsToDollars(
                parseInt(quoteResponse.data.attributes.fees.transactionFee, 10) || 0,
                quoteResponse.data.attributes.sellCurrency
            ),
            ...(quoteResponse.data.attributes.fees.fxMarginPercent && {
                fxMarginPercent: quoteResponse.data.attributes.fees.fxMarginPercent,
            }),
        },
        fixedSide: quoteResponse.data.attributes.fixedSide,
        wholesaleRate: parseFloat(quoteResponse.data.attributes.wholesaleRate),
        retailRate: parseFloat(quoteResponse.data.attributes.retailRate),
        validUntil: quoteResponse.data.attributes.validUntil,
    };
};
